import fetchBuilder from "@/utils/retry-utils";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.WARN ) );

import {
    Accepted,
    ApiError,
    BadRequest,
    Forbidden,
    InternalError,
    MultipleChoices,
    NoContent,
    NotFound,
    Ok,
    Unauthorized,
    UnprocessableContent
} from "@/types/api/responses";
import { RuntimeException } from '@/types/errors';
import type {
    ApiFetchResponse,
    BadRequestError,
    ErrorMessage,
    ErrorMessages,
    FetchOptions,
    UnprocessableContentError,
    TokenMessage
} from "@/types/api/fetch";
import { clientStateManager } from "@/services/error-services";

export const defaultReasonForAbortingApiCall: string = "The API call has timed out.";
const userNotAuthorized: string = 'AUTH_NOT_AUTHORIZED'.toLowerCase();
const noValidCredentialsMessage: string = 'The current user is not authorized to access this resource'.toLowerCase();
const emptyResponseMessage: string = 'Unexpected end of JSON input'.toLowerCase();

function parseForUnauthorizedErrorBecauseWeDontDoAnythingConsistently( error: ErrorMessage ): boolean {
    return error.extensions.code?.toLowerCase().includes( userNotAuthorized ) ||
        error.extensions.HttpsStatusCode === Unauthorized.statusCode;
}

function parseForEmptyResponse( msg: string | undefined | null ): boolean {
    return msg?.toLowerCase().includes( emptyResponseMessage ) ?? false;
}

export function isOkStatus( status: number ): boolean {
    // Accept ONLY status code 200
    return status === Ok.statusCode;
}

export function isSuccessState( status?: number | undefined | null ): boolean {
    // Accepts anything within the 200 - 299 range
    return !!status && ( status >= Ok.statusCode && status < MultipleChoices.statusCode );
}

export function responseHasNoContent( status: number ): boolean {
    // Accept ONLY status code 204
    return NoContent.statusCode === status;
}

export function contentNotFound( status: number ): boolean {
    // Accept ONLY status code 404
    return NotFound.statusCode === status;
}

export const defaultTimeoutInMilliseconds = 7000;
export function normalizeFetchParameters< OT >( { serverBaseUri, verbOverride, timeout, endpoint, payload, forceThrowExceptions }: FetchOptions< OT > ): FetchParameters {
    const baseUri: string = serverBaseUri ?? `${ import.meta.env.VITE_FLAGSHIP_BASE_URI }`;
    return {
        baseUri: baseUri,
        method: verbOverride ?? 'POST',
        uri: `${baseUri}${endpoint}`,
        body: payload ? JSON.stringify( payload ) : null,
        maxApiDuration: timeout ?? defaultTimeoutInMilliseconds,
        forceThrowExceptions: forceThrowExceptions ?? false
    };
}

export function buildStandardHeaders( authToken?: string | null | undefined ): HeadersInit {
    const headers: HeadersInit = {
        'Access-Control-Allow-Origin': `${ import.meta.env.VITE_ORIGIN }`,
        'Access-Control-Max-Age': '86400',
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }

    if ( authToken ) {
        headers.Authorization = `Bearer ${ authToken }`;
    }

    return headers;
}

type FetchParameters = {
    baseUri: string,
    method: string,
    uri: string,
    body: string | null,
    maxApiDuration: number,
    forceThrowExceptions: boolean
}

async function handleErrorConditions( errors: ErrorMessage[] | undefined, reject: ( reason: any ) => void ): Promise< void > {
    if ( errors ) {
        for ( const e of errors ) {
            if ( parseForUnauthorizedErrorBecauseWeDontDoAnythingConsistently( e ) ) {
                reject( new Unauthorized() );
                return;
            }

            if ( e.message.toLowerCase().includes( noValidCredentialsMessage ) ) {
                reject( new Forbidden() );
                return;
            }
        }
    }
}

export async function goFetch< T, OT >(
        { serverBaseUri, endpoint, verbOverride, payload, authToken, timeout, forceThrowExceptions }: FetchOptions< OT > ):
            Promise< ApiFetchResponse< T > | null | any > {

    if ( clientStateManager.isFaulted() ) {
        clientStateManager.handleClientError();
        return;
    }

    const fetchParams = normalizeFetchParameters( {
        serverBaseUri,
        verbOverride,
        timeout,
        endpoint,
        payload,
        forceThrowExceptions
    } );

    const headers = buildStandardHeaders( authToken );

    // Create a new Promise to encapsulate the asynchronous API call
    return new Promise( ( resolve, reject ) => {
        // Create an AbortController to short-circuit an API call that is taking too long
        const monitor = new AbortController();
        const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

        // Wrap the fetch network call with a retry handler
        const defaultRetryOptions = {
            retries: 3,
            // Exponential back-off
            retryDelay: ( attempt: number ): number => (
                Math.pow( 2, attempt ) * 1000
            ),
            // Any http response status codes that are related to timeouts or failures due to services being unavailable
            retryOn: [ 408, 460, 503, 504, 524, 598, 599 ]
        };

        const fetcher = fetchBuilder( fetch, defaultRetryOptions );

        // Perform the API call using fetch or any other suitable method
        fetcher( fetchParams.uri,  {
            method: fetchParams.method,
            body: fetchParams.body,
            headers: headers,
            signal: monitor.signal
        } ).then( async ( response: Response ): Promise< void > => {
            // Clear the timout at the earliest point to avoid false negative results
            clearTimeout( timeoutId );

            logger.debug( 'response received: ', response );
            if ( response.ok ) {
                let result: any = {};
                try {
                    result = await response.json();
                } catch ( err: any ) { /* IGNORE */ }

                const hasErrorsProperty = result.hasOwnProperty( 'errors' );
                if ( hasErrorsProperty ) {
                    await handleErrorConditions( result.errors, reject );
                }

                const hasDataProperty = result.hasOwnProperty('data' );
                // Resolve the Promise with the response data
                resolve( {
                    data: hasDataProperty ? result.data : result,
                    status: response.status
                } );
            } else {
                // As a generally good practice, always clean up any resources that may or may not be active
                clearTimeout( timeoutId );

                // First, parse the response text to determine if the error is something that can be handled
                const text: string = await response.text();
                let responseText: ErrorMessages | undefined;

                try
                {
                    responseText = JSON.parse( text );
                    await handleErrorConditions( responseText?.errors, reject );
                } catch ( err: any ) { /* IGNORE */ }

                // Reject the Promise with the original reason
                const statusCode = response.status;
                logger.debug( `API call failed with status: ${ statusCode }` );

                // TODO: Support any other errors that are seen regularly
                // TODO: Add in automatic client logging & metrics collection
                switch ( response.status ) {
                    case Unauthorized.statusCode:
                        reject( new Unauthorized() );
                        break;
                    case Forbidden.statusCode:
                        reject( new Forbidden() );
                        break;
                    case NotFound.statusCode:
                        reject( new NotFound() );
                        break;
                    case UnprocessableContent.statusCode:
                        try
                        {
                            const error: UnprocessableContentError = JSON.parse( text );
                            const tokenMessage: TokenMessage = JSON.parse( error.detail );
                            reject( new RuntimeException( tokenMessage.token[ 0 ] ) );
                        } catch { reject( new RuntimeException( '' ) );}
                        break;
                    case BadRequest.statusCode:
                        try{
                            const errorMessage: BadRequestError = JSON.parse( text );
                            reject( new RuntimeException( errorMessage.Message ) );
                        } catch { reject( new RuntimeException( '' ) );}
                        break;
                    case InternalError.statusCode:
                        console.error( 'THIS IS THE CAPTURING CONTEXT: ', response );
                        reject( new InternalError() );
                        break;
                    default:
                        reject( new ApiError( response.status, response.statusText ) );
                        break;
                }
            }
        } )
        // If the timeout was triggered, any access of the response after the face will throw an AbortError exception
        .catch( ( err: any ) => {
            // As a generally good practice, always clean up any resources that may or may not be active
            clearTimeout( timeoutId );

            // Automatically log the error message
            if ( typeof err === 'string' ) {
                logger.warn( err );
            } else {
                const message = JSON.stringify( err.message );
                if ( parseForEmptyResponse( message ) ) {
                    logger.warn( 'Empty response received' );
                    resolve( {
                        data: {} as T,
                        status: Accepted.statusCode
                    } );
                } else {
                    logger.warn( message );
                }
            }

            // TODO: Add in automatic metrics and client error monitoring

            // Reject the Promise with any error that occurs during the API call
            reject( err );
        } );
    } ).catch( ( err: any ) => {
        if(fetchParams.forceThrowExceptions) {
            throw err;
        }
        else if ( typeof err === 'object' && !( err instanceof RuntimeException )) {
            logger.warn( err );
            clientStateManager.queueClientError( err, {} );
            return;
        }
        else {
            throw err;
        }
    } );
}
